import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft } from 'lucide-react';
import { getLineItemGlDefaults, searchGlAccounts, setLineItemGlDefault } from '@/api/contracts';
import { EntityPicker, type PickerOption } from '@/components/EntityPicker';
import { ApiError } from '@/api/client';

/**
 * Contract line-item GL defaults (admin config). Sets the default GL account
 * applied to each line-item option (Asset / Software pool / SIM plan) when a
 * contract line is added — still editable per line. GL accounts come from
 * oscar.vGlAccount (expense P&L accounts), searched by code or description.
 */
const KINDS: { kind: string; label: string }[] = [
  { kind: 'Asset', label: 'Asset' },
  { kind: 'SoftwarePool', label: 'Software pool' },
  { kind: 'SimPlan', label: 'SIM plan' },
];

const glSearch = (q: string, signal?: AbortSignal): Promise<PickerOption[]> =>
  searchGlAccounts(q, signal).then((list) => list.map((g) => ({ id: g.GlAccountId, label: `${g.AccountCode} — ${g.AccountDescription}` })));

export function ContractGlDefaults() {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const query = useQuery({ queryKey: ['line-item-gl-defaults'], queryFn: ({ signal }) => getLineItemGlDefaults(signal) });
  const mutation = useMutation({
    mutationFn: ({ kind, glAccountId }: { kind: string; glAccountId: number }) => setLineItemGlDefault(kind, glAccountId),
    onSuccess: () => { setError(null); queryClient.invalidateQueries({ queryKey: ['line-item-gl-defaults'] }); },
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Could not save the default.'),
  });

  const defaults = query.data ?? [];
  const currentFor = (kind: string) => defaults.find((d) => d.LineItemKind === kind);

  return (
    <section className="max-w-2xl">
      <header className="mb-5">
        <Link to="/contracts" className="mb-2 inline-flex items-center gap-1 text-xs font-semibold text-darkGrey hover:text-navy">
          <ArrowLeft className="h-3.5 w-3.5" aria-hidden /> Contracts
        </Link>
        <h1 className="font-heading text-2xl font-bold text-navy">Contract line-item GL defaults</h1>
        <p className="mt-1 text-sm text-darkGrey/80">
          The default GL account applied to each line-item type when a contract line is added. It pre-fills the line&rsquo;s GL account and stays editable per line.
        </p>
      </header>

      {error && <div role="alert" className="mb-3 rounded border border-red/30 bg-red/10 px-3 py-2 text-sm text-red">{error}</div>}
      {query.isLoading && <p className="text-sm text-darkGrey/60">Loading…</p>}
      {query.isError && <p className="text-sm text-red">Could not load the defaults.</p>}

      {!query.isLoading && !query.isError && (
        <div className="space-y-2">
          {KINDS.map(({ kind, label }) => {
            const cur = currentFor(kind);
            const curLabel = cur ? (cur.AccountCode ? `${cur.AccountCode} — ${cur.AccountDescription ?? ''}` : `#${cur.GlAccountId}`) : '';
            return (
              <div key={kind} className="flex items-center gap-3 rounded border border-border bg-white px-3 py-2 shadow-sm">
                <span className="w-28 shrink-0 text-sm font-semibold text-navy">{label}</span>
                <div className="flex-1">
                  <EntityPicker
                    key={`${kind}-${cur?.GlAccountId ?? 'none'}`}
                    queryKey={`gl-default-${kind}`}
                    search={glSearch}
                    minChars={1}
                    valueLabel={curLabel}
                    onChange={(id) => { if (id) mutation.mutate({ kind, glAccountId: Number(id) }); }}
                    placeholder="Search GL by code or description"
                    inputClassName="w-full rounded border border-border bg-white px-2 py-1.5 text-sm"
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
